/**
 * App Staff (formerly "Platform Users").
 *
 * Lists Vanguard Beacon's own team. Customer staff live on the Tenants page
 * inside their company's expandable panel — never here.
 *
 * Tier permissions:
 *   admin    — full control. Can add / edit / remove other staff.
 *   manager  — full customer-side control. Cannot manage VB staff.
 *   staff    — same customer-side control as manager. Cannot manage VB staff.
 *   observer — read-only across the whole admin console.
 */
import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import {
  KeyRound, Copy, Check, Pencil, UserPlus, X, Trash2, Shield, Sparkles,
} from "lucide-react";
import { generateTempPassword } from "@/lib/tempPassword";

const TIER_META = {
  admin:    { label: "Admin",    cls: "ok",  desc: "Full control · can add/edit/remove VB staff" },
  manager:  { label: "Manager",  cls: "",    desc: "Full customer-side control · cannot manage VB staff" },
  staff:    { label: "Staff",    cls: "",    desc: "Helps tenants & manages enquiries · cannot manage VB staff" },
  observer: { label: "Observer", cls: "warn", desc: "Read-only across the admin console" },
  sales:    { label: "Sales",    cls: "",    desc: "Runs client demos · dashboard, tenants, tiers, rollout & demo setup" },
};

const tierOf = (u) => (u.staff_tier || "admin").toLowerCase();
const isAdminTier = (u) => tierOf(u) === "admin";
const canEditStaff = (me) => isAdminTier(me);

export default function AppStaff() {
  const { user: me } = useAuth();
  const { confirm, notify } = useDialog();
  const [staff, setStaff] = useState([]);
  const [search, setSearch] = useState("");
  const [tempPwd, setTempPwd] = useState(null);
  const [copied, setCopied] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    // Only show platform users (no tenant) — customer staff move to the Tenants page.
    setStaff((data.users || []).filter((u) => !u.tenant_id && u.role === "super_admin"));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return staff;
    return staff.filter((u) =>
      u.name.toLowerCase().includes(q)
      || (u.email || "").toLowerCase().includes(q)
      || tierOf(u).includes(q)
    );
  }, [staff, search]);

  const resetPwd = async (u) => {
    if (!await confirm({
      title: `Reset ${u.name}'s password?`,
      message: "We'll generate a temporary password to share with them. They'll be required to set their own password on next login.",
      confirmLabel: "Generate temp password",
    })) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setTempPwd({ user: u, password: data.temporary_password });
      setCopied(false);
    } catch (e) {
      await notify({ title: "Reset failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const removeStaff = async (u) => {
    if (u.id === me.id) return;
    if (!await confirm({
      title: `Remove ${u.name} from VB staff?`,
      message: `${u.email} will lose all access to the admin console immediately. This cannot be undone.`,
      danger: true, confirmLabel: "Remove staff",
    })) return;
    try { await api.delete(`/admin/platform-users/${u.id}`); await load(); }
    catch (e) {
      await notify({ title: "Remove failed", message: formatErr(e.response?.data?.detail), variant: "err" });
    }
  };

  const copyTemp = async () => {
    try { await navigator.clipboard.writeText(tempPwd.password); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { console.debug("Clipboard write failed:", e); }
  };

  const myTier = tierOf(me);
  const writeable = myTier !== "observer";

  return (
    <div className="vb-fade-in" data-testid="app-staff-page">
      <div className="flex justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>App Staff</h1>
          <p className="opacity-70 max-w-2xl">
            The Vanguard Beacon team. Each member's <strong>tier</strong> controls what they can do here.
            Customer staff live on the <strong>Tenants</strong> page inside their company.
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <input
            className="vb-input"
            placeholder="Search name, email, tier…"
            value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="staff-search" style={{ minWidth: 260 }}
          />
          {canEditStaff(me) && (
            <button className="vb-btn" onClick={() => setShowAdd(true)} data-testid="add-staff-btn">
              <UserPlus size={14} /> Add staff
            </button>
          )}
        </div>
      </div>

      {!writeable && (
        <div className="vb-pill warn mb-4 inline-flex items-center" data-testid="observer-banner">
          <Shield size={12} /> You're viewing in <strong className="ml-1">Observer</strong> mode — read-only.
        </div>
      )}

      <div className="vb-card overflow-x-auto" data-testid="staff-table-wrap">
        <table className="vb-table">
          <thead>
            <tr><th>Name</th><th>Email</th><th>Tier</th><th>Joined</th><th style={{ width: 140 }}></th></tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 28 }} className="opacity-60">No staff match.</td></tr>
            )}
            {filtered.map((u) => {
              const tier = tierOf(u);
              const meta = TIER_META[tier] || TIER_META.staff;
              const isMe = u.id === me.id;
              return (
                <tr key={u.id} data-testid={`staff-row-${u.id}`}>
                  <td>
                    <strong>{u.name}</strong>
                    {isMe && <span className="vb-pill ok ml-2 text-[10px]">you</span>}
                    {u.must_reset_password && <span className="vb-pill warn ml-2 text-[10px]">pwd reset pending</span>}
                  </td>
                  <td>{u.email}</td>
                  <td>
                    <span className={`vb-pill ${meta.cls}`} title={meta.desc} data-testid={`tier-${u.id}`}>{meta.label}</span>
                  </td>
                  <td className="opacity-70">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                  <td>
                    <div className="flex gap-1 justify-end items-center" style={{ whiteSpace: "nowrap" }}>
                      {canEditStaff(me) && (
                        <button className="vb-btn ghost sm" title="Edit" onClick={() => setEditing(u)} data-testid={`edit-staff-${u.id}`}>
                          <Pencil size={12} />
                        </button>
                      )}
                      {canEditStaff(me) && (
                        <button className="vb-btn ghost sm" title="Reset password" onClick={() => resetPwd(u)} data-testid={`reset-pwd-${u.id}`}>
                          <KeyRound size={13} />
                        </button>
                      )}
                      {canEditStaff(me) && !isMe && (
                        <button className="vb-btn ghost sm" title="Remove staff" onClick={() => removeStaff(u)}
                          style={{ color: "var(--vb-error)" }}
                          data-testid={`remove-staff-${u.id}`}>
                          <Trash2 size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {tempPwd && (
        <div className="vb-modal-bg">
          <div className="vb-modal">
            <h2>Temporary password for {tempPwd.user.name}</h2>
            <p className="text-sm opacity-80 mb-3">
              Share this securely (e.g. over a phone call). The user will be required to set their own password on next login.
            </p>
            <div className="vb-card p-4 flex items-center justify-between gap-3 mb-4 font-mono text-lg" data-testid="temp-pwd-display">
              {tempPwd.password}
              <button className="vb-btn ghost" onClick={copyTemp} data-testid="copy-temp">
                {copied ? <Check size={14} /> : <Copy size={14} />} {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <div className="text-right">
              <button className="vb-btn" onClick={() => setTempPwd(null)} data-testid="close-temp">Close</button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <AddStaffModal
          onClose={() => setShowAdd(false)}
          onSaved={async (msg) => {
            setShowAdd(false);
            await load();
            await notify({ title: "Staff added", message: msg, variant: "ok" });
          }}
        />
      )}

      {editing && (
        <EditStaffModal
          me={me} user={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}

function TierLegend() {
  return (
    <div className="text-[11px] opacity-70 space-y-0.5">
      {Object.entries(TIER_META).map(([k, m]) => (
        <div key={k}><strong>{m.label}:</strong> {m.desc}</div>
      ))}
    </div>
  );
}

function AddStaffModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", staff_tier: "staff" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/platform-users", {
        email: form.email.trim(),
        password: form.password,
        name: form.name.trim(),
        staff_tier: form.staff_tier,
      });
      const tierLabel = TIER_META[data.user.staff_tier]?.label || data.user.staff_tier;
      const emailNote = data.email_sent
        ? `A welcome email with login details has been sent to ${data.user.email}.`
        : `Email delivery is not configured — please share these credentials with them directly.`;
      onSaved(`${data.user.name} (${tierLabel}) created. ${emailNote} They'll be asked to set a new password on first login.`);
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="add-staff-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="add-staff-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">Add VB staff</h2>
            <p className="text-xs opacity-60 m-0">A new member of the Vanguard Beacon team.</p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="add-staff-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="add-staff-form">
          <div>
            <label className="text-xs opacity-70">Full name *</label>
            <input className="vb-input" required maxLength={120} value={form.name} onChange={set("name")} data-testid="new-staff-name" />
          </div>
          <div>
            <label className="text-xs opacity-70">Email *</label>
            <input className="vb-input" required type="email" value={form.email} onChange={set("email")} data-testid="new-staff-email" />
          </div>
          <div>
            <label className="text-xs opacity-70">Initial password *</label>
            <div className="flex gap-2 items-stretch">
              <input
                className="vb-input flex-1"
                required minLength={6} type="text"
                value={form.password} onChange={set("password")}
                placeholder="Min 6 chars — they'll reset on first login"
                data-testid="new-staff-password"
              />
              <button
                type="button"
                className="vb-btn ghost"
                onClick={() => setForm((f) => ({ ...f, password: generateTempPassword(12) }))}
                title="Generate a strong random password"
                data-testid="new-staff-generate-pwd"
                style={{ whiteSpace: "nowrap" }}
              >
                <Sparkles size={13} /> Generate
              </button>
            </div>
            <p className="text-[11px] opacity-60 mt-1">
              We'll email this to them along with the login link (if Resend is configured).
            </p>
          </div>
          <div>
            <label className="text-xs opacity-70">Tier *</label>
            <select className="vb-input" value={form.staff_tier} onChange={set("staff_tier")} data-testid="new-staff-tier">
              <option value="admin">Admin — full control + manage VB staff</option>
              <option value="manager">Manager — full customer-side control</option>
              <option value="staff">Staff — helps tenants &amp; enquiries</option>
              <option value="observer">Observer — read-only</option>
              <option value="sales">Sales — client demos</option>
            </select>
            <p className="text-[11px] opacity-60 mt-1">Admin has full control incl. managing VB staff. Sales gets a limited console for running client demos.</p>
          </div>
          <details className="text-xs opacity-70">
            <summary className="cursor-pointer">What does each tier do?</summary>
            <div className="mt-2"><TierLegend /></div>
          </details>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="new-staff-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="new-staff-submit">
              {busy ? "Creating…" : "Create staff"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditStaffModal({ me, user, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: user.name || "",
    email: user.email || "",
    staff_tier: tierOf(user),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = {};
      if (form.name.trim() !== (user.name || "")) payload.name = form.name.trim();
      if (form.email.trim().toLowerCase() !== (user.email || "")) payload.email = form.email.trim();
      if (form.staff_tier !== tierOf(user)) payload.staff_tier = form.staff_tier;
      if (Object.keys(payload).length === 0) { onClose(); return; }
      await api.put(`/admin/users/${user.id}`, payload);
      onSaved();
    } catch (ex) { setErr(formatErr(ex.response?.data?.detail) || "Update failed"); }
    finally { setBusy(false); }
  };

  const isSelf = me.id === user.id;

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="edit-staff-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }} data-testid="edit-staff-modal">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl m-0">Edit staff</h2>
            <p className="text-xs opacity-60 m-0">Fix typos in name / email or change tier.</p>
          </div>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="edit-staff-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="edit-staff-form">
          <div>
            <label className="text-xs opacity-70">Full name *</label>
            <input className="vb-input" required maxLength={120} value={form.name} onChange={set("name")} data-testid="edit-staff-name" />
          </div>
          <div>
            <label className="text-xs opacity-70">Email *</label>
            <input className="vb-input" required type="email" value={form.email} onChange={set("email")} data-testid="edit-staff-email" />
            {user.email !== form.email.trim().toLowerCase() && (
              <p className="text-[11px] opacity-60 mt-1">Changing email also changes the login identifier — let the user know before saving.</p>
            )}
          </div>
          <div>
            <label className="text-xs opacity-70">Tier *</label>
            <select className="vb-input" value={form.staff_tier} onChange={set("staff_tier")} data-testid="edit-staff-tier">
              <option value="admin">Admin — full control + manage VB staff</option>
              <option value="manager">Manager — full customer-side control</option>
              <option value="staff">Staff — helps tenants &amp; enquiries</option>
              <option value="observer">Observer — read-only</option>
              <option value="sales">Sales — client demos</option>
            </select>
            {isSelf && form.staff_tier !== "admin" && (
              <p className="text-[11px] mt-1" style={{ color: "var(--vb-error)" }}>
                Demoting yourself only works while at least one other Admin remains.
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="edit-staff-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="edit-staff-submit">
              {busy ? "Saving…" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
